// src/components/DailyActivityModal.jsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserActivity } from "../../redux/actions/AttendanceReportActions";
import dayjs from "dayjs";

const DailyActivityModal = ({ onClose, user = "User", date, status }) => {
  const dispatch = useDispatch();
  const isoDate = dayjs(date).format("YYYY-MM-DD");

  const { activityData, loading } = useSelector(
    (state) => state.attendanceReport
  );

  useEffect(() => {
    dispatch(fetchUserActivity(isoDate));
  }, [dispatch, isoDate]);

  const attendance = activityData?.attendance || {};
  const tracking = activityData?.tracking || [];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "present": return "text-green-600";
      case "absent": return "text-red-500";
      case "leave": return "text-orange-500";
      case "holiday": return "text-yellow-600";
      case "pending": return "text-blue-500";
      default: return "text-gray-600";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-[92%] max-w-md overflow-y-auto max-h-[92vh]">

        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Daily Activity Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 text-2xl font-bold hover:text-red-500 transition"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* User Info */}
        <div className="bg-blue-50 text-center py-4 px-5">
          <h3 className="text-xl font-bold text-gray-800">
            {attendance.user_name || user}
          </h3>
          <p className="text-sm text-gray-500 mt-1">{date}</p>
          {attendance.office_timing && (
            <p className="text-xs text-blue-600 font-medium mt-1">
              🕐 Office Timing: {attendance.office_timing}
            </p>
          )}
          <span
            className={`inline-block mt-2 text-sm font-semibold capitalize ${getStatusColor(
              status || attendance.status
            )}`}
          >
            ● {status || attendance.status || "Unknown"}
          </span>
        </div>

        <div className="px-5 py-4 space-y-5">

          {loading && (
            <p className="text-center text-gray-400 py-6">Loading activity...</p>
          )}

          {!loading && (
            <>
              {/* Check In / Check Out */}
              <Section title="📍 Attendance Details">
                <div className="flex gap-3">
                  <CheckCard
                    label="Check In"
                    time={attendance.check_in_time}
                    address={attendance.check_in_address}
                    photo={attendance.check_in_photo}
                  />
                  <CheckCard
                    label="Check Out"
                    time={attendance.check_out_time}
                    address={attendance.check_out_address}
                    photo={attendance.check_out_photo}
                  />
                </div>
              </Section>

              {/* Remarks */}
              {attendance.remarks && (
                <InfoRow label="Remarks" value={attendance.remarks} capitalize />
              )}

              {/* Work Time Summary */}
              <Section title="⏱ Work Time Summary">
                <div className="grid grid-cols-2 gap-3">
                  <TimeCard
                    label="Total Worked"
                    value={attendance.total_worked}
                    color="text-green-600"
                    bg="bg-green-50"
                  />
                  <TimeCard
                    label="Extra Duty"
                    value={attendance.extra_duty}
                    color="text-blue-600"
                    bg="bg-blue-50"
                  />
                  <TimeCard
                    label="Short Duty"
                    value={attendance.short_duty}
                    color="text-red-500"
                    bg="bg-red-50"
                  />
                  <TimeCard
                    label="Late Clock In"
                    value={attendance.late_clock_in}
                    color="text-orange-500"
                    bg="bg-orange-50"
                  />
                  <TimeCard
                    label="Early Clock Out"
                    value={attendance.early_clock_out}
                    color="text-purple-500"
                    bg="bg-purple-50"
                  />
                </div>
              </Section>

              {/* Tracking Info */}
              {tracking.length > 0 && (
                <Section title="🗺 Tracking Info">
                  {tracking.map((track, index) => (
                    <div key={index} className="bg-gray-50 rounded-xl p-3 mb-2">
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>Start:</strong> {track.start_time} @ {track.start_address}
                      </p>
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>End:</strong> {track.end_time} @ {track.end_address}
                      </p>
                      <p className="text-sm text-gray-500 italic">
                        Status: {track.status}
                      </p>
                    </div>
                  ))}
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Sub-components ── */

const Section = ({ title, children }) => (
  <div>
    <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
    {children}
  </div>
);

const CheckCard = ({ label, time, address, photo }) => (
  <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
    <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
    <p className="text-blue-600 font-semibold text-sm">
      {time || "Not recorded"}
    </p>
    {address && (
      <p className="text-xs text-gray-400 mt-1 leading-snug">{address}</p>
    )}
    {photo && (
      <img
        src={photo}
        alt={label}
        className="w-14 h-14 rounded-full object-cover mx-auto mt-2 border-2 border-blue-200"
      />
    )}
  </div>
);

const TimeCard = ({ label, value, color, bg }) => (
  <div className={`${bg} rounded-xl p-3 text-center`}>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`text-sm font-bold ${color}`}>{value || "—"}</p>
  </div>
);

const InfoRow = ({ label, value, capitalize }) => (
  <div className="flex justify-between text-sm text-gray-700 bg-gray-50 rounded-lg px-4 py-2">
    <span className="font-medium text-gray-500">{label}</span>
    <span className={`font-semibold ${capitalize ? "capitalize" : ""}`}>{value}</span>
  </div>
);

export default DailyActivityModal;